import {
  Badge,
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormLabel,
  HStack,
  Stack,
  Text,
  useBoolean,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import { useSearchSuppliersApi } from 'apis/supplier/use-search-suppliers-api';
import { BaseFilterButton } from 'components/base-table/filters/base-filter-button';
import { useDebounce } from 'hooks/use-debounce';
import { usePageParams } from 'hooks/use-page-params';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiCheck, FiSearch } from 'react-icons/fi';

type TInputs = {
  supplierId: string;
  supplier: { value: number; label: string } | null;
};

const defaultValues: TInputs = {
  supplierId: '',
  supplier: null,
};

export const SupplierFilterButton = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();

  // Search state for async select
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Search API
  const { data: searchResults, isLoading: isSearching } = useSearchSuppliersApi(debouncedSearchTerm, {
    enabled: debouncedSearchTerm.length >= 2,
  });

  const suppliers = searchResults?.data || [];

  // Transform suppliers to options for ChakraAsyncSelect
  const supplierOptions = useMemo(() => {
    return suppliers.map((supplier) => ({
      value: supplier.id,
      label: supplier.name,
      data: supplier, // Keep full supplier data for rich display
    }));
  }, [suppliers]);

  // Update active filter button
  useEffect(() => {
    if (params.supplierId) setIsActive.on();
    else setIsActive.off();
  }, [params.supplierId, setIsActive]);

  const { handleSubmit, formState, reset, setValue, watch, register } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  const selectedSupplier = watch('supplier');

  // Update values when params change
  useEffect(() => {
    if (params.supplierId) {
      setValue('supplierId', String(params.supplierId));
      // Try to find the supplier from search results if available
      const foundSupplier = suppliers.find((s) => s.id === Number(params.supplierId));
      if (foundSupplier) {
        setValue('supplier', {
          value: foundSupplier.id,
          label: foundSupplier.name,
        });
      }
    } else {
      setValue('supplierId', '');
      setValue('supplier', null);
    }
  }, [params.supplierId, suppliers, setValue]);

  // Load supplier options for async select - simplified approach
  const loadSupplierOptions = useCallback(
    (inputValue: string) => {
      setSearchTerm(inputValue);

      if (inputValue.length < 2) {
        return Promise.resolve([]);
      }

      // Return current options - the API hook will handle the actual fetching
      return Promise.resolve(supplierOptions);
    },
    [supplierOptions]
  );

  const onSubmit = (data: TInputs) => {
    if (data.supplier?.value) {
      params.supplierId = String(data.supplier.value);
    } else {
      delete params.supplierId;
    }

    setPageParams(params);
    onClose();
    reset(data);
  };

  const handleClose = () => {
    onClose();
    if (params.supplierId) {
      setValue('supplierId', String(params.supplierId));
      const foundSupplier = suppliers.find((s) => s.id === Number(params.supplierId));
      if (foundSupplier) {
        setValue('supplier', {
          value: foundSupplier.id,
          label: foundSupplier.name,
        });
      }
    } else {
      setValue('supplierId', '');
      setValue('supplier', null);
    }
  };

  const handleDisableFilterButton = () => {
    return Object.keys(formState.dirtyFields).length === 0;
  };

  const handleSupplierChange = useCallback(
    (selectedOption: any) => {
      setValue('supplier', selectedOption);
      setValue('supplierId', selectedOption ? String(selectedOption.value) : '');
    },
    [setValue]
  );

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={isActive}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleSubmit(onSubmit)}>
          <FormControl>
            <FormLabel>Search Supplier</FormLabel>
            <ChakraAsyncSelect
              value={selectedSupplier}
              onChange={handleSupplierChange}
              loadOptions={loadSupplierOptions}
              defaultOptions={[]} // Start with empty, load on search
              placeholder='🔍 Search suppliers by name...'
              isClearable
              isLoading={isSearching}
              noOptionsMessage={({ inputValue }) =>
                inputValue && inputValue.length >= 2 ? 'No suppliers found' : 'Min 2 characters to search'
              }
              loadingMessage={() => 'Searching suppliers...'}
              styles={{
                control: (provided: any) => ({
                  ...provided,
                  borderRadius: '8px',
                  borderColor: '#E2E8F0',
                  boxShadow: '0 0 0 1px #E2E8F0',
                  '&:hover': { borderColor: '#CBD5E0' },
                }),
                menu: (provided: any) => ({
                  ...provided,
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }),
                option: (provided: any, state: any) => ({
                  ...provided,
                  padding: '12px 16px',
                  backgroundColor: state.isSelected ? '#3182CE' : state.isFocused ? '#E6FFFA' : 'white',
                  color: state.isSelected ? 'white' : '#2D3748',
                  '&:hover': {
                    backgroundColor: state.isSelected ? '#3182CE' : '#E6FFFA',
                  },
                }),
              }}
              formatOptionLabel={(option: any, { context }) => {
                if (context === 'menu') {
                  // Find the full supplier data for rich display
                  const supplierData = suppliers.find((s) => s.id === option.value);
                  return (
                    <VStack align='start' spacing={2} w='100%'>
                      <HStack spacing={2} w='100%' align='start'>
                        <VStack align='start' spacing={1} flex='1' minW='0'>
                          <Text
                            fontWeight='semibold'
                            fontSize='sm'
                            lineHeight='1.2'
                            wordBreak='break-word'
                            whiteSpace='normal'
                            noOfLines={2}
                            w='100%'>
                            {option.label}
                          </Text>
                          <HStack spacing={3} fontSize='xs' color='gray.500' flexWrap='wrap'>
                            <Text>ID: {supplierData?.id}</Text>
                            {supplierData?.isActive === false && (
                              <Badge colorScheme='red' size='xs'>
                                Inactive
                              </Badge>
                            )}
                          </HStack>
                        </VStack>
                      </HStack>
                    </VStack>
                  );
                }
                return (
                  <Text wordBreak='break-word' whiteSpace='normal' noOfLines={1}>
                    {option.label}
                  </Text>
                );
              }}
            />
          </FormControl>

          {/* Hidden field for form submission */}
          <input type='hidden' {...register('supplierId')} />

          {/* Selected supplier preview */}
          {selectedSupplier && (
            <VStack
              p={3}
              bg='green.50'
              borderRadius='md'
              border='1px solid'
              borderColor='green.200'
              align='stretch'
              spacing={2}>
              <HStack spacing={3}>
                <FiCheck color='green' />
                <VStack align='start' spacing={1} flex='1'>
                  <Text fontSize='sm' fontWeight='semibold' color='green.800'>
                    Selected Supplier
                  </Text>
                  <Text fontSize='md' fontWeight='bold' color='green.600'>
                    {selectedSupplier.label}
                  </Text>
                </VStack>
                <Badge colorScheme='green'>ID: {selectedSupplier.value}</Badge>
              </HStack>

              {/* Show additional details if available */}
              {(() => {
                const supplierData = suppliers.find((s) => s.id === selectedSupplier.value);
                if (supplierData) {
                  return (
                    <VStack align='start' spacing={1} fontSize='xs' color='gray.600' pl={6}>
                      {supplierData.countryCode && <Text>🌍 {supplierData.countryCode}</Text>}
                    </VStack>
                  );
                }
                return null;
              })()}
            </VStack>
          )}

          <HStack justify='flex-end'>
            <Button variant='outline' colorScheme='red' onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant='solid'
              colorScheme='red'
              type='submit'
              disabled={handleDisableFilterButton()}
              leftIcon={<FiSearch />}>
              Filter
            </Button>
          </HStack>
        </Stack>
      }
    />
  );
};
